import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DiscountCodeForm from './form'
import { getAppliedCouponTranslation } from './utils'
import {
    useApplyReservationCoupon,
    useRemoveReservationCoupon,
} from '@/hooks/api/use-reservation-checkout'
import type { ActiveReservationCoupon } from '@/types/reservation-checkout'

export default function DiscountCode({
    currentlyAppliedCoupon,
}: {
    currentlyAppliedCoupon: ActiveReservationCoupon | null
}) {
    const { t } = useTranslation()
    const [edit, setEdit] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [requestInProgress, setRequestInProgress] = useState(false)
    const appliedCouponTranslation = currentlyAppliedCoupon
        ? getAppliedCouponTranslation(currentlyAppliedCoupon)
        : null

    const applyReservationCoupon = useApplyReservationCoupon({
        setRequestInProgress,
        onErrorCallback: message => {
            setErrorMessage(message)
        },
        onSuccessCallback: () => {
            setErrorMessage(null)
            setEdit(false)
        },
    })

    const removeReservationCoupon = useRemoveReservationCoupon({
        setRequestInProgress,
        onErrorCallback: message => {
            setErrorMessage(message)
        },
        onSuccessCallback: () => {
            setErrorMessage(null)
            setEdit(false)
        },
    })

    const handleSubmit = async (discountCode: string) => {
        const normalizedDiscountCode = discountCode.trim()

        if (!normalizedDiscountCode) {
            setErrorMessage(t('checkout.discountCode.validation.required'))
            return
        }

        setErrorMessage(null)
        setRequestInProgress(true)

        try {
            await applyReservationCoupon.mutateAsync({
                coupon_code: normalizedDiscountCode,
            })
        } catch {
            return
        }
    }

    const handleEdit = () => {
        setErrorMessage(null)
        setEdit(true)
    }

    const handleRemove = async () => {
        setErrorMessage(null)
        setRequestInProgress(true)

        try {
            await removeReservationCoupon.mutateAsync()
        } catch {
            return
        }
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}>
            {/* <Typography
                level="body-md"
                sx={{
                    fontWeight: 600,
                    textAlign: 'center',
                }}
            >
                {t('checkout.discountCode.heading')}
            </Typography> */}
            {edit ? (
                <DiscountCodeForm
                    initialCode=""
                    requestInProgress={requestInProgress}
                    errorMessage={errorMessage}
                    onSubmit={handleSubmit}
                    onChangeStart={() => {
                        if (errorMessage) {
                            setErrorMessage(null)
                        }
                    }}
                />
            ) : currentlyAppliedCoupon ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                }}>
                    <Typography level="body-sm">
                        {appliedCouponTranslation
                            ? t(appliedCouponTranslation.key, appliedCouponTranslation.values)
                            : null}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                            variant="plain"
                            onClick={handleEdit}
                            disabled={requestInProgress}
                            sx={{
                                color: 'black',
                                textDecoration: 'underline',
                            }}
                        >
                            {t('checkout.discountCode.changeCode')}
                        </Button>
                        <Button
                            variant="plain"
                            onClick={handleRemove}
                            disabled={requestInProgress}
                            sx={{
                                color: 'black',
                                textDecoration: 'underline',
                            }}
                        >
                            {t('checkout.discountCode.removeCode')}
                        </Button>
                    </Box>
                    {errorMessage ? (
                        <Typography level="body-xs" color="danger">
                            {errorMessage}
                        </Typography>
                    ) : null}
                </Box>
            ) : (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <Button
                        variant="plain"
                        onClick={handleEdit}
                        sx={{
                            color: 'black',
                            textDecoration: 'underline',
                        }}
                    >
                        {t('checkout.discountCode.addCode')}
                    </Button>
                </Box>
            )}
        </Box>
    )
}
